import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAllowances, useUpsertAllowance } from '../../hooks';
import { Plus, X } from 'lucide-react';
import { useToast } from '../../components/ui/toast';

interface Allowance {
  id: string;
  emp_code: string;
  category: string;
  amount: number;
  effective_from: string;
  effective_to?: string | null;
  note?: string | null;
}

type FormData = {
  emp_code: string; category: string; amount: number; effective_from: string; note?: string;
};

function AddModal({ onClose }: { onClose: () => void }) {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
  const upsert = useUpsertAllowance();
  const toast = useToast();

  async function onSubmit(data: FormData) {
    try {
      await upsert.mutateAsync({ ...data, amount: Number(data.amount) } as Record<string, unknown>);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">เพิ่มเงินเพิ่ม</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="label">รหัสพนักงาน *</label>
            <input {...register('emp_code', { required: true })} className="input" />
            {errors.emp_code && <p className="text-xs text-red-500 mt-1">กรุณากรอกรหัสพนักงาน</p>}
          </div>
          <div>
            <label className="label">ประเภท *</label>
            <input {...register('category', { required: true })} className="input" placeholder="เช่น ค่าน้ำมัน, ค่าที่พัก" />
          </div>
          <div>
            <label className="label">จำนวนเงิน (บาท) *</label>
            <input type="number" step="0.01" {...register('amount', { required: true, min: 0 })} className="input" />
          </div>
          <div>
            <label className="label">มีผลตั้งแต่ *</label>
            <input type="date" {...register('effective_from', { required: true })} className="input" />
          </div>
          <div>
            <label className="label">หมายเหตุ</label>
            <input {...register('note')} className="input" />
          </div>
          <button type="submit" disabled={upsert.isPending} className="btn-primary w-full">
            {upsert.isPending ? 'กำลังบันทึก...' : 'เพิ่ม'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function HRAllowances() {
  const { data, isLoading } = useAllowances() as { data: Allowance[] | undefined; isLoading: boolean };
  const [showModal, setShowModal] = useState(false);

  // Group by emp_code
  const grouped = (data ?? []).reduce<Record<string, Allowance[]>>((acc, a) => {
    (acc[a.emp_code] ??= []).push(a);
    return acc;
  }, {});

  if (isLoading) return <AdminLayout title="เงินเพิ่ม (รายคน)"><p className="py-8 text-center text-gray-500">กำลังโหลด...</p></AdminLayout>;

  return (
    <AdminLayout
      title="เงินเพิ่ม (รายคน)"
      actions={
        <button onClick={() => setShowModal(true)} className="btn-primary btn-slim">
          <Plus className="w-3.5 h-3.5" /> เพิ่ม
        </button>
      }
    >
      {Object.entries(grouped).map(([emp_code, items]) => (
        <div key={emp_code} className="mb-4">
          <p className="text-sm font-semibold text-gray-600 mb-1">พนักงาน: {emp_code}</p>
          <div className="space-y-1">
            {items.map((a) => (
              <div key={a.id} className="card p-3 flex justify-between items-center">
                <div>
                  <p className="font-medium text-sm">{a.category}</p>
                  <p className="text-xs text-gray-500">มีผลตั้งแต่ {a.effective_from}</p>
                </div>
                <span className="font-semibold text-brand-600">
                  {a.amount.toLocaleString('th-TH')} บาท
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {Object.keys(grouped).length === 0 && (
        <p className="text-center text-gray-500 py-8">ยังไม่มี Allowance</p>
      )}
      {showModal && <AddModal onClose={() => setShowModal(false)} />}
    </AdminLayout>
  );
}
